import React from "react";
import { useTranslation } from "react-i18next";
import { BLOCK_TYPES } from "./formDocumentSchema";

function FieldLabel({ label, required }) {
  if (!label) return null;
  return (
    <label className="mb-1.5 block text-sm font-medium text-slate-700">
      {label}
      {required ? <span className="text-rose-500"> *</span> : null}
    </label>
  );
}

function BlockHeading({ block, mode, onChange }) {
  const { text, level = 2 } = block.props || {};
  const Tag = `h${Math.min(Math.max(level, 1), 3)}`;
  const className =
    level === 1
      ? "text-2xl font-semibold text-slate-950"
      : level === 2
        ? "text-xl font-semibold text-slate-900"
        : "text-lg font-semibold text-slate-800";

  if (mode === "builder") {
    return (
      <input
        type="text"
        value={text || ""}
        onChange={(e) => onChange?.({ ...block.props, text: e.target.value })}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-lg font-semibold text-slate-900"
        placeholder="Overskrift"
      />
    );
  }

  return <Tag className={className}>{text}</Tag>;
}

function BlockParagraph({ block, mode, onChange }) {
  const content = block.props?.content || "";

  if (mode === "builder") {
    return (
      <textarea
        value={content.replace(/<[^>]+>/g, "")}
        onChange={(e) =>
          onChange?.({ ...block.props, content: `<p>${e.target.value}</p>` })
        }
        rows={3}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700"
        placeholder="Instruksjonstekst"
      />
    );
  }

  return (
    <div
      className="prose prose-sm max-w-none text-slate-700"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}

function BlockCallout({ block, mode, onChange }) {
  const { title, content, variant = "info" } = block.props || {};
  const styles =
    variant === "warning"
      ? "border-amber-300 bg-amber-50 text-amber-950"
      : "border-blue-300 bg-blue-50 text-blue-950";

  if (mode === "builder") {
    return (
      <div className={`rounded-2xl border-l-4 p-4 ${styles}`}>
        <input
          type="text"
          value={title || ""}
          onChange={(e) => onChange?.({ ...block.props, title: e.target.value })}
          className="mb-2 w-full bg-transparent text-sm font-semibold outline-none"
        />
        <textarea
          value={(content || "").replace(/<[^>]+>/g, "")}
          onChange={(e) =>
            onChange?.({ ...block.props, content: `<p>${e.target.value}</p>` })
          }
          rows={2}
          className="w-full bg-transparent text-sm outline-none"
        />
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border-l-4 p-4 ${styles}`}>
      {title ? <div className="mb-1 text-sm font-semibold">{title}</div> : null}
      <div
        className="prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </div>
  );
}

function BlockTextField({ block, value, onValueChange, readOnly }) {
  const { label, placeholder, required } = block.props || {};
  return (
    <div>
      <FieldLabel label={label} required={required} />
      <input
        type="text"
        value={value ?? ""}
        disabled={readOnly}
        placeholder={placeholder || ""}
        onChange={(e) => onValueChange?.(e.target.value)}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none disabled:bg-slate-50"
      />
    </div>
  );
}

function BlockTextarea({ block, value, onValueChange, readOnly }) {
  const { label, rows = 4, required } = block.props || {};
  return (
    <div>
      <FieldLabel label={label} required={required} />
      <textarea
        rows={rows}
        value={value ?? ""}
        disabled={readOnly}
        onChange={(e) => onValueChange?.(e.target.value)}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none disabled:bg-slate-50"
      />
    </div>
  );
}

function BlockNumber({ block, value, onValueChange, readOnly }) {
  const { label, required } = block.props || {};
  return (
    <div>
      <FieldLabel label={label} required={required} />
      <input
        type="number"
        value={value ?? ""}
        disabled={readOnly}
        onChange={(e) => onValueChange?.(e.target.value)}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none disabled:bg-slate-50"
      />
    </div>
  );
}

function BlockDate({ block, value, onValueChange, readOnly }) {
  const { label, required } = block.props || {};
  return (
    <div>
      <FieldLabel label={label} required={required} />
      <input
        type="date"
        value={value ?? ""}
        disabled={readOnly}
        onChange={(e) => onValueChange?.(e.target.value)}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none disabled:bg-slate-50"
      />
    </div>
  );
}

function BlockCheckbox({ block, value, onValueChange, readOnly }) {
  const { label, required } = block.props || {};
  return (
    <label className="flex items-start gap-3 text-sm text-slate-800">
      <input
        type="checkbox"
        checked={Boolean(value)}
        disabled={readOnly}
        onChange={(e) => onValueChange?.(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900"
      />
      <span>
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </span>
    </label>
  );
}

function BlockSelect({ block, value, onValueChange, readOnly }) {
  const { label, options = [], required } = block.props || {};
  return (
    <div>
      <FieldLabel label={label} required={required} />
      <select
        value={value ?? ""}
        disabled={readOnly}
        onChange={(e) => onValueChange?.(e.target.value)}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none disabled:bg-slate-50"
      >
        <option value="">—</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function BlockChecklist({ block, value, onValueChange, readOnly }) {
  const { label, items = [] } = block.props || {};
  const checked = value?.checked || {};

  return (
    <div>
      <FieldLabel label={label} />
      <div className="space-y-2">
        {items.map((item) => {
          const key = item.id || item.label;
          return (
            <label key={key} className="flex items-center gap-3 text-sm text-slate-800">
              <input
                type="checkbox"
                checked={Boolean(checked[key])}
                disabled={readOnly}
                onChange={(e) =>
                  onValueChange?.({
                    checked: { ...checked, [key]: e.target.checked },
                  })
                }
                className="h-4 w-4 rounded border-slate-300 text-slate-900"
              />
              <span>{item.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function BlockTable({ block, value, onValueChange, readOnly }) {
  const { t } = useTranslation();
  const { label, columns = [], allowAddRows = true } = block.props || {};
  const rows = value?.rows || [];

  const updateCell = (rowIndex, colId, cellValue) => {
    const nextRows = rows.map((row, i) =>
      i === rowIndex ? { ...row, [colId]: cellValue } : row
    );
    onValueChange?.({ rows: nextRows });
  };

  const addRow = () => {
    const emptyRow = columns.reduce((acc, col) => {
      acc[col.id] = col.type === "checkbox" ? false : "";
      return acc;
    }, {});
    onValueChange?.({ rows: [...rows, emptyRow] });
  };

  const removeRow = (rowIndex) => {
    onValueChange?.({ rows: rows.filter((_, i) => i !== rowIndex) });
  };

  return (
    <div>
      <FieldLabel label={label} required={block.props?.required} />
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.id}
                  className="px-3 py-2 text-left font-semibold text-slate-700"
                >
                  {col.label}
                </th>
              ))}
              {!readOnly && rows.length > 1 ? (
                <th className="w-10 px-2 py-2" />
              ) : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((col) => (
                  <td key={col.id} className="px-3 py-2 align-top">
                    {col.type === "checkbox" ? (
                      <input
                        type="checkbox"
                        checked={Boolean(row[col.id])}
                        disabled={readOnly}
                        onChange={(e) =>
                          updateCell(rowIndex, col.id, e.target.checked)
                        }
                        className="h-4 w-4 rounded border-slate-300"
                      />
                    ) : (
                      <input
                        type={
                          col.type === "number"
                            ? "number"
                            : col.type === "date"
                              ? "date"
                              : "text"
                        }
                        value={row[col.id] ?? ""}
                        disabled={readOnly}
                        onChange={(e) =>
                          updateCell(rowIndex, col.id, e.target.value)
                        }
                        className="w-full min-w-[120px] rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                      />
                    )}
                  </td>
                ))}
                {!readOnly && rows.length > 1 ? (
                  <td className="px-2 py-2">
                    <button
                      type="button"
                      onClick={() => removeRow(rowIndex)}
                      className="text-xs text-rose-500 hover:text-rose-700"
                    >
                      {t("Delete")}
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && allowAddRows ? (
        <button
          type="button"
          onClick={addRow}
          className="mt-2 text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          + {t("documentForms.table.add_row")}
        </button>
      ) : null}
    </div>
  );
}

function BlockSignature({ block, readOnly }) {
  const { label } = block.props || {};
  return (
    <div className="mt-6 border-t border-slate-200 pt-6">
      <div className="text-sm font-medium text-slate-700">
        {label || "Signatur"}
      </div>
      <div className="mt-8 h-12 max-w-xs border-b border-slate-900" />
      {!readOnly ? (
        <p className="mt-2 text-xs text-slate-500">
          Signaturfelt vises på utskrift/PDF
        </p>
      ) : null}
    </div>
  );
}

export function FormDocumentBlock({
  block,
  mode = "fill",
  value,
  onValueChange,
  onPropsChange,
  readOnly = false,
}) {
  if (!block) return null;

  switch (block.type) {
    case BLOCK_TYPES.HEADING:
      return (
        <BlockHeading block={block} mode={mode} onChange={onPropsChange} />
      );
    case BLOCK_TYPES.PARAGRAPH:
      return (
        <BlockParagraph block={block} mode={mode} onChange={onPropsChange} />
      );
    case BLOCK_TYPES.CALLOUT:
      return (
        <BlockCallout block={block} mode={mode} onChange={onPropsChange} />
      );
    case BLOCK_TYPES.DIVIDER:
      return <hr className="my-4 border-slate-200" />;
    case BLOCK_TYPES.TEXT_FIELD:
      return (
        <BlockTextField
          block={block}
          value={value}
          onValueChange={onValueChange}
          readOnly={readOnly}
        />
      );
    case BLOCK_TYPES.TEXTAREA:
      return (
        <BlockTextarea
          block={block}
          value={value}
          onValueChange={onValueChange}
          readOnly={readOnly}
        />
      );
    case BLOCK_TYPES.NUMBER:
      return (
        <BlockNumber
          block={block}
          value={value}
          onValueChange={onValueChange}
          readOnly={readOnly}
        />
      );
    case BLOCK_TYPES.DATE:
      return (
        <BlockDate
          block={block}
          value={value}
          onValueChange={onValueChange}
          readOnly={readOnly}
        />
      );
    case BLOCK_TYPES.CHECKBOX:
      return (
        <BlockCheckbox
          block={block}
          value={value}
          onValueChange={onValueChange}
          readOnly={readOnly}
        />
      );
    case BLOCK_TYPES.SELECT:
      return (
        <BlockSelect
          block={block}
          value={value}
          onValueChange={onValueChange}
          readOnly={readOnly}
        />
      );
    case BLOCK_TYPES.CHECKLIST:
      return (
        <BlockChecklist
          block={block}
          value={value}
          onValueChange={onValueChange}
          readOnly={readOnly}
        />
      );
    case BLOCK_TYPES.TABLE:
      return (
        <BlockTable
          block={block}
          value={value}
          onValueChange={onValueChange}
          readOnly={readOnly}
        />
      );
    case BLOCK_TYPES.SIGNATURE:
      return <BlockSignature block={block} readOnly={readOnly} />;
    default:
      return null;
  }
}
